// May 2026 monthly report companion blog.
import { defineDocument, em, link, p, section, strong, text, } from '../../schema';
const mayReportHref = '/party-central/reports/2026-05-top-party-games-and-streamers';
export const blogPartyGameStreamingByTheNumbersMay2026Document = defineDocument({
    id: 'blog:party-game-streaming-by-the-numbers-may-2026',
    kind: 'blog',
    slug: 'party-game-streaming-by-the-numbers-may-2026',
    route: '/blog/party-game-streaming-by-the-numbers-may-2026',
    seo: {
        title: 'Party Game Streaming by the Numbers: May 2026',
        description: 'nanos world deepened its hold at #1, The House Always Wins launched strong, and PiaPiUFO topped the May 2026 streamer rankings.',
        imageAsset: 'twitchStats202605',
        imageAlt: 'Collage of top party games and streamers from the May 2026 Twitch data report',
    },
    hero: {
        eyebrow: [text('Airhead Blog')],
        title: [text('Party Game Streaming by the Numbers: May 2026')],
        description: [
            text('nanos world extends its reign, The House Always Wins kicks off a strong launch, and PiaPiUFO jumps to #1 on the streamer leaderboard.'),
        ],
        imageAsset: 'twitchStats202605',
        imageAlt: 'Collage of top party games and streamers from the May 2026 Twitch data report',
        caption: 'Top party games and streamers on Twitch for May 2026, as tracked by the Party Central report.',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-06-02T00:00:00.000Z'),
        updatedAt: new Date('2026-06-02T00:00:00.000Z'),
        tags: ['party-central'],
    },
    sections: [
        section({
            id: 'article',
            blocks: [
                p([
                    text('May was a quieter month than April: total streams fell 5% and unique streamers fell 10%. Even so, we found fascinating stories inside the more than '),
                    strong('185,000 streams'),
                    text(' we tracked on Twitch from '),
                    strong('74,000 streamers'),
                    text(' across '),
                    strong('829 games'),
                    text(' and '),
                    strong('35 languages'),
                    text('.'),
                ]),
                p([
                    text('The full May 2026 party game streaming report '),
                    link(mayReportHref, 'is now available.'),
                ]),
            ],
            sections: [
                section({
                    id: 'article__nanos-world-gets-stickier',
                    title: 'nanos world Gets Stickier',
                    blocks: [
                        p([
                            em('nanos world'),
                            text(" held the #1 spot by Game Party Score for the second month in a row. Stream count rose 31% from last month, with the bulk of the increase coming from the same players coming back even more often. Stickiness was up from 10.6 streams per streamer in April to 13.1 in May, the highest stickiness score we've seen from any game so far in 2026."),
                        ]),
                        p([
                            text('This is the same French community that drove the April breakout, still playing '),
                            em('Zenkai'),
                            text(', the ninja role-playing game server built on the platform, with the same devotion. A small group of streamers including '),
                            link('https://twitch.tv/miidouw_', 'Miidouw_'),
                            text(', '),
                            link('https://twitch.tv/walidoen17', 'walidoen17'),
                            text(', and '),
                            link('https://twitch.tv/wamoun', 'Wamoun'),
                            text(' led the repeat play. While not the most widely streamed game, receiving the top Party Score reflects how Zenkai has turned into a daily habit for many French streamers and that their audiences are coming along for the ride, averaging an impressive 56 viewers per stream.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__just-dance-rises-mario-kart-hits-the-brakes',
                    title: 'Just Dance Rises, Mario Kart Hits The Brakes',
                    blocks: [
                        p([
                            text('The most notable movement in the Party Score rankings was '),
                            em('Just Dance 2026 Edition'),
                            text(' climbing from #9 to #3. The across-the-board improvement saw streams rise 34% and average viewers increase 38% from last month.'),
                        ]),
                        p([
                            em('Mario Kart World'),
                            text(' moved in the opposite direction, slipping from #3 to #8 as stream count, streamer breadth, and average viewers all softened from April. It remains a large game by raw volume, but it seems enthusiasm has cooled for now. '),
                            em('Words On Stream'),
                            text(' strengthened its lock on the #2 spot by Party Score, the same rank as last month, by maintaining its high stickiness while also reaching more streamers.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__the-house-always-wins-scores-a-jackpot',
                    title: 'The House Always Wins Scores a Jackpot',
                    blocks: [
                        p([
                            text('May had a smaller release slate than recent months, with only 22 new party games hitting the shelves. The quality of the debuts was the story, though. Four releases cleared 100 streams compared with only two in prior months.'),
                        ]),
                        p([
                            em('The House Always Wins'),
                            text(' was the strongest pickup, generating 502 streams from 319 streamers in the 10 days following its May 21 launch. The game is a 1-4 player co-op casino management sim where players build the business, keep slot machines running, and react to chaotic visitors including vandals and alien guests. The concept translated well to audiences with popular creators like '),
                            link('https://twitch.tv/xqc', 'xQc'),
                            text(' bringing nearly 22,000 viewers to the title.'),
                        ]),
                        p([
                            em('Bow-wow Battle'),
                            text(' launched May 1 and also saw immediate success on stream. '),
                            link('https://twitch.tv/rubius', 'Rubius'),
                            text(' and the Spanish community helped the game to reach #8 on the Global Games with Biggest Audiences rankings.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__piapiufo-slimes-the-streamer-board',
                    title: 'PiaPiUFO Slimes the Streamer Board',
                    blocks: [
                        p([
                            link('https://twitch.tv/piapiufo', 'PiaPiUFO'),
                            text(' rose from #7 to #1 on the Streamer Party Score leaderboard with a jump that was hard to miss: 12 captures in April became 47 in May. Forty-two of those came on '),
                            em('Words On Stream'),
                            text(', which was enough to make the channel a major reason that game held its #2 ranking by Party Score. Average viewers stayed above 500 throughout.'),
                        ]),
                        p([
                            link('https://twitch.tv/chilledchaos', 'ChilledChaos'),
                            text(' drew the biggest average audience in the top 10 with nearly 3,200 viewers per stream, playing eight different party games including '),
                            em('Among Us'),
                            text(' and '),
                            em('Jackbox Party Packs'),
                            text('. '),
                            link('https://twitch.tv/karacorvus', 'KaraCorvus'),
                            text(' made the Party Score top 15 again for the fourth straight month, a level of consistency that puts her in rare company. '),
                            link('https://twitch.tv/partychads', 'PARTYCHADS'),
                            text(' led versatility again, rotating through 16 games and anchoring the retro party-game scene.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__mamma-mia-italian-viewership-soars',
                    title: 'Mamma Mia! Italian Viewership Soars',
                    blocks: [
                        p([
                            text('While overall streaming volume was down this month, several language communities grew: Portuguese, Japanese, Italian, and Arabic all added activity. Average viewership for Italian streams skyrocketed from 26 to 183, almost entirely because of '),
                            link('https://twitch.tv/tumblurr', 'Tumblurr'),
                            text('. Their six streams across five games produced more than 200,000 viewers.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__read-the-full-report',
                    title: 'Read the Full Report',
                    blocks: [
                        p([
                            text('There is a lot more in the '),
                            link(mayReportHref, 'full report'),
                            text('. You will find the complete Party Score rankings for every game and streamer, the full slate of new releases, streamer spotlights, and deep dives by game and language.'),
                        ]),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=party-game-streaming-by-the-numbers-may-2026.js.map